(() => {
  'use strict';

  const seenVideos = new WeakSet();
  const seenDeferred = new WeakSet();

  function shouldSkip(video) {
    return Boolean(
      video.closest?.(
        '.dili-review-card, [data-dili-review-modal], .dili-review-viewer, .dili-reviews-page'
      )
    );
  }

  function prepareVideo(video) {
    if (!video || shouldSkip(video)) return;

    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;

    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');

    if (!video.getAttribute('preload') || video.getAttribute('preload') === 'none') {
      video.setAttribute('preload', 'metadata');
    }

    if (!seenVideos.has(video)) {
      seenVideos.add(video);
      video.addEventListener('loadedmetadata', () => tryPlay(video), { passive: true });
      video.addEventListener('canplay', () => {
        if (isVisible(video)) tryPlay(video);
      }, { passive: true });
      videoObserver.observe(video);
    }

    if (isVisible(video)) tryPlay(video);
  }

  function tryPlay(video) {
    if (!video || document.hidden || !isVisible(video)) return;
    const promise = video.play?.();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  }

  function isVisible(el) {
    if (!el?.isConnected) return false;
    const rect = el.getBoundingClientRect();
    const height = window.innerHeight || document.documentElement.clientHeight;
    const width = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.right > 0 && rect.top < height && rect.left < width;
  }

  function normalizeIframe(iframe) {
    if (!iframe?.src) return;
    try {
      const url = new URL(iframe.src, window.location.href);
      if (url.hostname.includes('youtube.com') || url.hostname.includes('youtube-nocookie.com')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
        url.searchParams.set('playsinline', '1');
        url.searchParams.set('loop', '1');
        const id = url.pathname.split('/').filter(Boolean).pop();
        if (id) url.searchParams.set('playlist', id);
      } else if (url.hostname.includes('vimeo.com')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('muted', '1');
        url.searchParams.set('loop', '1');
        url.searchParams.set('playsinline', '1');
      } else {
        return;
      }
      const next = url.toString();
      if (iframe.src !== next) iframe.src = next;
    } catch (_) {}
  }

  function resumeVisibleVideos() {
    document.querySelectorAll('video').forEach((video) => {
      if (!shouldSkip(video) && isVisible(video)) {
        prepareVideo(video);
        tryPlay(video);
      }
    });
  }

  function loadDeferredMedia(node) {
    if (!node || node.hasAttribute('loaded')) {
      node?.querySelectorAll?.('video').forEach(prepareVideo);
      node?.querySelectorAll?.('iframe').forEach(normalizeIframe);
      return;
    }

    const template = node.querySelector('template');
    if (!template) return;

    const templateMedia = template.content?.querySelector?.('video, iframe');
    if (!templateMedia) return;

    try {
      if (typeof node.loadContent === 'function') {
        node.loadContent(false);
      } else {
        const clone = template.content.firstElementChild?.cloneNode(true);
        if (clone) {
          node.setAttribute('loaded', true);
          node.appendChild(clone);
        }
      }
    } catch (_) {}

    requestAnimationFrame(() => {
      node.querySelectorAll('video').forEach(prepareVideo);
      node.querySelectorAll('iframe').forEach(normalizeIframe);
      resumeVisibleVideos();
    });
  }

  const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio > 0.05) {
        prepareVideo(video);
        tryPlay(video);
      } else if (!entry.isIntersecting) {
        // Pause off-screen playback to preserve battery/data. It resumes automatically.
        try { video.pause?.(); } catch (_) {}
      }
    });
  }, { rootMargin: '180px 0px', threshold: [0, 0.05, 0.25] });

  const deferredObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) loadDeferredMedia(entry.target);
    });
  }, { rootMargin: '240px 0px', threshold: 0.01 });

  function scan(scope = document) {
    scope.querySelectorAll?.('video').forEach(prepareVideo);

    scope.querySelectorAll?.('deferred-media').forEach((node) => {
      if (node.closest?.('.dili-review-card, [data-dili-review-modal], .dili-review-viewer')) return;
      if (seenDeferred.has(node)) return;
      const template = node.querySelector('template');
      if (!template?.content?.querySelector?.('video, iframe')) return;
      seenDeferred.add(node);
      deferredObserver.observe(node);
    });

    scope.querySelectorAll?.('iframe').forEach((iframe) => {
      if (isVisible(iframe)) normalizeIframe(iframe);
    });
  }

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('video')) prepareVideo(node);
        else scan(node);
      });
    });
  });

  function boot() {
    scan(document);
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('pageshow', () => setTimeout(() => {
      scan(document);
      resumeVisibleVideos();
    }, 80), { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) resumeVisibleVideos();
    });
    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target);
      setTimeout(resumeVisibleVideos, 100);
    });
    window.addEventListener('load', () => {
      scan(document);
      resumeVisibleVideos();
    }, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
